(() => {
  'use strict';

  const copy = {
    nl: {
      title: 'Vorm in<br><em>beweging.</em>',
      intro: 'De contouren. De gloed. Het detail. Van dichtbij.',
      discover: 'Ontdek',
      source: 'Originele studiofilms van Leora',
      note: 'Bekijk de gekozen uitvoering en afmetingen bij het product.',
      play: 'Film afspelen',
      pause: 'Film pauzeren',
      playAction: 'Afspelen',
      pauseAction: 'Pauzeren',
      unavailable: 'Film niet beschikbaar'
    },
    de: {
      title: 'Form in<br><em>Bewegung.</em>',
      intro: 'Die Konturen. Der Schein. Das Detail. Aus der Nähe.',
      discover: 'Entdecke',
      source: 'Originale Studiofilme von Leora',
      note: 'Die gewählte Ausführung und Maße findest du beim Produkt.',
      play: 'Film abspielen',
      pause: 'Film pausieren',
      playAction: 'Abspielen',
      pauseAction: 'Pausieren',
      unavailable: 'Film nicht verfügbar'
    }
  };
  const films = [
    { model: '10', video: 'lighting-10-film-v1.mp4', poster: 'lighting-10-film-poster-v1.webp' },
    { model: '07', color: 'natural-amber', video: 'lighting-07-natural-film-v1.mp4', poster: 'lighting-07-natural-film-poster-v1.webp' }
  ];
  const mounted = new WeakMap();
  const playIcon = '<svg class="yv-film-play-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M6 3.5 16 10 6 16.5z"/></svg>';
  const pauseIcon = '<svg class="yv-film-pause-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M5 4h4v12H5zm6 0h4v12h-4z"/></svg>';
  const arrowIcon = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M4 10h12m-5-5 5 5-5 5"/></svg>';

  function escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  }

  function selection(settings, film) {
    const product = settings.products?.find(candidate => candidate.id === film.model);
    if (!product || typeof settings.productUrl !== 'function') return null;
    if (!film.color) return product;
    const choice = product.colorChoices?.find(candidate => candidate.key === film.color && (settings.preview === true || (candidate.native === true && /^[1-9]\d*$/.test(String(candidate.variantId || '')))));
    if (!choice || typeof window.YaficoVariants?.select !== 'function') return null;
    const selected = window.YaficoVariants.select(settings, product, film.color);
    return selected.colorKey === film.color && String(selected.variantId) === String(choice.variantId) ? selected : null;
  }

  function home(settings = {}) {
    const words = copy[settings.locale] || copy.nl;
    const asset = typeof settings.asset === 'function' ? settings.asset : filename => filename;
    const cards = films.flatMap(film => {
      const product = selection(settings, film);
      if (!product) return [];
      const name = product.name;
      const colorLabel = product.colorLabel || (settings.locale === 'de' ? 'Orange' : 'Oranje');
      const label = film.color ? `${name} · ${colorLabel}` : name;
      return [`<article class="yv-film-card" data-film-card><div class="yv-film-player"><video data-studio-film data-film-src="${escape(asset(film.video))}" poster="${escape(asset(film.poster))}" width="720" height="1280" preload="none" muted loop playsinline aria-label="${escape(label)}"></video><button type="button" class="yv-film-toggle" data-film-toggle data-playing="false" aria-pressed="false" aria-label="${escape(`${words.play} · ${label}`)}">${playIcon}${pauseIcon}<span data-film-action>${words.playAction}</span></button></div><div class="yv-film-caption"><h3>${escape(name)}</h3>${film.color ? `<span class="yv-film-color">${escape(colorLabel)}</span>` : ''}<a class="yv-film-link" href="${escape(settings.productUrl(product))}">${words.discover} ${escape(name)}${arrowIcon}</a></div></article>`];
    });
    if (!cards.length) return '';
    return `<section class="yv-film-section" id="in-motion" aria-labelledby="yv-film-title"><div class="yv-film-layout"><header class="yv-film-intro"><p class="yb-kicker">YAFICO / IN MOTION</p><h2 id="yv-film-title">${words.title}</h2><p>${words.intro}</p></header><div class="yv-film-grid${cards.length === 1 ? ' yv-film-grid-single' : ''}">${cards.join('')}</div></div><p class="yv-film-source"><span>${words.source}</span>${words.note}</p></section>`;
  }

  function mount(settings = {}) {
    const root = settings.root;
    if (!root?.querySelectorAll) return;
    mounted.get(root)?.();
    mounted.delete(root);
    const videos = Array.from(root.querySelectorAll('[data-studio-film]'));
    if (!videos.length) return;
    const words = copy[settings.locale] || copy.nl;
    const document = root.ownerDocument || window.document;
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let disposed = false;
    let observer;
    const records = videos.map(video => ({ video, button: video.closest('[data-film-card]')?.querySelector('[data-film-toggle]'), inView: false, manuallyPaused: false, userStarted: false, wantsPlay: false, request: 0 }));

    function update(record, playing) {
      const button = record.button;
      if (!button) return;
      button.dataset.playing = String(playing);
      button.setAttribute('aria-pressed', String(playing));
      button.setAttribute('aria-label', `${playing ? words.pause : words.play} · ${record.video.getAttribute('aria-label') || ''}`);
      const action = button.querySelector('[data-film-action]');
      if (action) action.textContent = playing ? words.pauseAction : words.playAction;
    }

    function sync(record) {
      if (disposed) return;
      const visible = document?.visibilityState !== 'hidden' && document?.hidden !== true;
      const shouldPlay = record.inView && visible && !record.manuallyPaused && !record.unavailable && (!motion?.matches || record.userStarted);
      record.wantsPlay = shouldPlay;
      const request = ++record.request;
      if (!shouldPlay) {
        record.video.pause();
        update(record, false);
        return;
      }
      record.video.muted = true;
      record.video.playsInline = true;
      if (!record.video.getAttribute('src')) {
        record.video.preload = 'metadata';
        record.video.src = record.video.dataset.filmSrc;
        record.video.load?.();
      }
      if (!record.video.paused) {
        update(record, true);
        return;
      }
      try {
        Promise.resolve(record.video.play()).then(() => {
          if (disposed) return;
          if (!record.wantsPlay) record.video.pause();
          else if (record.request === request) update(record, !record.video.paused);
        }).catch(() => {
          if (record.request !== request || disposed) return;
          record.wantsPlay = false;
          update(record, false);
        });
      } catch {
        record.wantsPlay = false;
        update(record, false);
      }
    }

    function boundsVisible(video) {
      const bounds = video.getBoundingClientRect?.();
      const height = window.innerHeight || document?.documentElement?.clientHeight || 0;
      const width = window.innerWidth || document?.documentElement?.clientWidth || 0;
      return !!bounds && bounds.bottom > 0 && bounds.top < height && bounds.right > 0 && bounds.left < width;
    }

    function checkBounds() {
      records.forEach(record => {
        record.inView = boundsVisible(record.video);
        sync(record);
      });
    }

    function visibilityChanged() {
      records.forEach(sync);
    }

    function motionChanged() {
      records.forEach(record => {
        record.userStarted = false;
        sync(record);
      });
    }

    records.forEach(record => {
      record.clicked = () => {
        if (record.wantsPlay || !record.video.paused) {
          record.manuallyPaused = true;
          record.userStarted = false;
        } else {
          record.manuallyPaused = false;
          record.userStarted = true;
          record.inView = boundsVisible(record.video);
        }
        sync(record);
      };
      record.played = () => update(record, true);
      record.paused = () => update(record, false);
      record.failed = () => {
        record.unavailable = true;
        record.wantsPlay = false;
        record.video.pause();
        update(record, false);
        if (record.button) {
          record.button.disabled = true;
          record.button.setAttribute('aria-label', words.unavailable);
          const action = record.button.querySelector('[data-film-action]');
          if (action) action.textContent = words.unavailable;
        }
      };
      record.button?.addEventListener('click', record.clicked);
      record.video.addEventListener('play', record.played);
      record.video.addEventListener('pause', record.paused);
      record.video.addEventListener('error', record.failed);
    });
    if (typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver(entries => {
        if (disposed) return;
        entries.forEach(entry => {
          const record = records.find(candidate => candidate.video === entry.target);
          if (!record) return;
          record.inView = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          sync(record);
        });
      }, { threshold: [0, 0.2] });
      records.forEach(record => observer.observe(record.video));
    } else {
      window.addEventListener?.('scroll', checkBounds, { passive: true });
      window.addEventListener?.('resize', checkBounds, { passive: true });
      checkBounds();
    }
    document?.addEventListener?.('visibilitychange', visibilityChanged);
    if (motion?.addEventListener) motion.addEventListener('change', motionChanged);
    else motion?.addListener?.(motionChanged);
    mounted.set(root, () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener?.('scroll', checkBounds);
      window.removeEventListener?.('resize', checkBounds);
      document?.removeEventListener?.('visibilitychange', visibilityChanged);
      if (motion?.removeEventListener) motion.removeEventListener('change', motionChanged);
      else motion?.removeListener?.(motionChanged);
      records.forEach(record => {
        record.button?.removeEventListener('click', record.clicked);
        record.video.removeEventListener('play', record.played);
        record.video.removeEventListener('pause', record.paused);
        record.video.removeEventListener('error', record.failed);
        record.video.pause();
      });
    });
  }

  window.YaficoVideo = { home, mount };
})();
